import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
    MobilePreviewPeek,
    PeekHint,
    PreviewFab,
    SheetBackdrop,
    SheetClose,
    SheetTitle,
    SheetStage,
    SheetScaler,
} from './OnboardingLayout.styles';
import { SignupAnalyticsContext } from '../../SignupAnalyticsContext';

/** Size of the phone in StoreLivePreview (frame included), used to fit it in the sheet. */
const PHONE_WIDTH = 320;
const PHONE_HEIGHT = 600;
/** Room the sheet keeps for its title, close button and margins. */
const SHEET_CHROME_HEIGHT = 112;
const SHEET_SIDE_MARGIN = 32;

const fitScale = () =>
    Math.min(
        1,
        (window.innerHeight - SHEET_CHROME_HEIGHT) / PHONE_HEIGHT,
        (window.innerWidth - SHEET_SIDE_MARGIN) / PHONE_WIDTH
    );

/**
 * Mobile / tablet (<1024px) store preview: the cut-off phone below the form, a floating
 * "Ver mi tienda" button while that phone is off screen, and a full-screen sheet (opened from
 * either) that shows the whole store scaled to the screen.
 */
const MobilePreview = ({ children }) => {
    const { t } = useTranslation();
    const { analytics, stepName } = useContext(SignupAnalyticsContext);
    const peekRef = useRef(null);
    const closeRef = useRef(null);
    const [peekVisible, setPeekVisible] = useState(false);
    const [open, setOpen] = useState(false);
    const [scale, setScale] = useState(1);

    // The floating button only shows while the inline preview is off screen.
    useEffect(() => {
        const node = peekRef.current;
        if (!node || typeof IntersectionObserver === 'undefined') return undefined;
        const observer = new IntersectionObserver(([entry]) => setPeekVisible(entry.isIntersecting), {
            threshold: 0.15,
        });
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    useLayoutEffect(() => {
        if (!open) return undefined;
        const update = () => setScale(fitScale());
        update();
        window.addEventListener('resize', update);
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKey = (event) => {
            if (event.key === 'Escape') setOpen(false);
        };
        document.addEventListener('keydown', onKey);
        if (closeRef.current) closeRef.current.focus();
        return () => {
            window.removeEventListener('resize', update);
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = previousOverflow;
        };
    }, [open]);

    /** @param {'peek' | 'button'} via the cut-off phone or the floating "Ver mi tienda" button */
    const show = (via) => {
        if (analytics) analytics.track('onboarding_preview_opened', { step_name: stepName, via });
        setOpen(true);
    };

    return (
        <>
            <MobilePreviewPeek
                ref={peekRef}
                role="button"
                tabIndex={0}
                onClick={() => show('peek')}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        show('peek');
                    }
                }}
                aria-label={t('SignUpV2.preview.open')}
            >
                {children}
                <PeekHint aria-hidden="true">
                    <i className="ri-fullscreen-line" />
                    {t('SignUpV2.preview.tapToExpand')}
                </PeekHint>
            </MobilePreviewPeek>

            {!peekVisible && !open && (
                <PreviewFab type="button" onClick={() => show('button')}>
                    <i className="ri-eye-line" aria-hidden="true" />
                    {t('SignUpV2.preview.open')}
                </PreviewFab>
            )}

            {open &&
                ReactDOM.createPortal(
                    <SheetBackdrop
                        role="dialog"
                        aria-modal="true"
                        aria-label={t('SignUpV2.preview.caption')}
                        onClick={(event) => {
                            if (event.target === event.currentTarget) setOpen(false);
                        }}
                    >
                        <SheetTitle>{t('SignUpV2.preview.caption')}</SheetTitle>
                        <SheetClose
                            ref={closeRef}
                            type="button"
                            onClick={() => setOpen(false)}
                            aria-label={t('SignUpV2.preview.close')}
                        >
                            <i className="ri-close-line" aria-hidden="true" />
                        </SheetClose>
                        <SheetStage style={{ width: PHONE_WIDTH * scale, height: PHONE_HEIGHT * scale }}>
                            <SheetScaler style={{ transform: `scale(${scale})` }}>{children}</SheetScaler>
                        </SheetStage>
                    </SheetBackdrop>,
                    document.body
                )}
        </>
    );
};

export default MobilePreview;
